import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SummaryInput from './components/SummaryInput';
import StoryPlayback from './components/StoryPlayback';
import Storyboard from './components/Storyboard';
import Home from './components/Home'; // Import the new Home component
import axios from 'axios';
import { MockData } from './models/MockData'; // Import mockStory from mockData.js
import PlayPage from './pages/PlayPage';
import AnalyticsService from "./services/AnalyticsService"; // Import the new service

const App = () => {
  const [story, setStory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false); // For enhancement loading
  const [isEditing, setIsEditing] = useState(true);
  const [openRouterConfig, setOpenRouterConfig] = useState({
    openRouterKey: '',
    openRouterModel: '',
    openRouterContent: '',
    openRouterEnhanceContent: '',
  });
  const [fetchError, setFetchError] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [enhancedStory, setEnhancedStory] = useState(null);
  const [assets, setAssets] = useState({
    characters: {}, // Maps character names to their images
    backgroundImage: null, // Stores the background image
    backgroundMusic: null, // Stores the background music
  });

  const [loadJsonOnInit] = useState(false);

  // Init Ananlytics Service
  useEffect(() => {
    AnalyticsService.initialize(); // Initialize GA on app load
    AnalyticsService.trackPageView("/", "App Loaded"); // Track initial page load
  }, []);

  // Fetch OpenRouter configuration on component mount
  useEffect(() => {

    // axios.interceptors.request.use(request => {
    //   console.log('Starting Request', JSON.stringify(request, null, 2))
    //   return request
    // })

    // axios.interceptors.response.use(response => {
    //   console.log('Response:', JSON.stringify(response, null, 2))
    //   return response
    // })


    const fetchOpenRouterConfig = async () => {
      try {

        const response = await axios.get(
          'https://api.jsonsilo.com/public/fc3b9aff-632c-4930-9328-1ae7ada73d46', {
          headers: {
            'Content-Type': 'application/json', // Set the Content-Type header
          },
        })
        console.log('response', response)
        setOpenRouterConfig(response.data);
      } catch (error) {
        setFetchError('Failed to fetch OpenRouter configuration. Using default settings.' + error);
        console.error(fetchError);
      }
    };

    if(loadJsonOnInit)
      fetchOpenRouterConfig();
  }, [fetchError, loadJsonOnInit]);


  const handleSelectStory = (selectedStory) => {
    setStory(selectedStory);
    setEnhancedStory(null); // Clear enhanced story after selection
  };

  const onPreFedStory = async () => {
    setIsLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      setStory(MockData); // Use the mock story object
      setIsEditing(true); // Show the storyboard for editing
      setIsLoading(false);

      // Set default assets for the Tortoise and Hare story
      setAssets({
        characters: MockData.assets.characters, // Set character images
        backgroundImage: MockData.assets.backgroundImage, // Set background image
        backgroundMusic: MockData.assets.backgroundMusic, // Set background music
      });

    }, 1000); // Simulate 1-second delay
  };

  const handleGenerateSummaries = async (summary) => {
    setIsLoading(true);

    try {
      // Call OpenRouter API to generate 3 expanded summaries
      const response = await axios.post(
        'https://openrouter.ai/api/v1/chat/completions',
        {
          model: openRouterConfig.openRouterModel,
          messages: [
            {
              role: 'user',
              content: openRouterConfig.openRouterSummaries.replace('${summary}', summary),
            },
          ],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${openRouterConfig.openRouterKey}`,
          },
        }
      );

      // Extract the generated content
      const generatedText = response.data.choices[0].message.content;

      // Extract the JSON string from the triple backticks
      const jsonStart = generatedText.indexOf('```json') + 7; // Start of JSON
      const jsonEnd = generatedText.lastIndexOf('```'); // End of JSON
      const jsonString = generatedText.slice(jsonStart, jsonEnd).trim();

      // Parse the JSON string into an object
      const { summaries } = JSON.parse(jsonString);

      // Set the summaries state (to be displayed in a new component)
      setSummaries(summaries);
    } catch (error) {
      console.error('Error generating summaries:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSummarySelection = async (selectedSummary) => {
    setIsLoading(true);

    try {
      // Call OpenRouter API to generate the full story based on the selected summary
      const response = await axios.post(
        'https://openrouter.ai/api/v1/chat/completions',
        {
          model: openRouterConfig.openRouterModel,
          messages: [
            {
              role: 'user',
              content: openRouterConfig.openRouterContent.replace('${summary}', selectedSummary),
            },
          ],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${openRouterConfig.openRouterKey}`,
          },
        }
      );

      // Extract and parse the story data
      const generatedText = response.data.choices[0].message.content;
      const jsonStart = generatedText.indexOf('```json') + 7;
      const jsonEnd = generatedText.lastIndexOf('```');
      const jsonString = generatedText.slice(jsonStart, jsonEnd).trim();
      const storyData = JSON.parse(jsonString);

      // Set the story state
      setStory(storyData);
    } catch (error) {
      console.error('Error generating story:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnhanceStory = async (enhancementSuggestion) => {
    setIsEnhancing(true); // Start enhancement loading

    try {
      // Call OpenRouter API with fetched configuration
      const response = await axios.post(
        'https://openrouter.ai/api/v1/chat/completions',
        {
          model: openRouterConfig.openRouterModel,
          messages: [
            {
              role: 'user',
              content: openRouterConfig.openRouterEnhanceContent
                .replace('${story}', JSON.stringify(story, null, 2))
                .replace('${enhancementSuggestion}', enhancementSuggestion),
            }
          ],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${openRouterConfig.openRouterKey}`,
          },
        }
      );

      // Extract the generated content
      const generatedText = response.data.choices[0].message.content;

      console.log('generatedText', generatedText)

      // Extract the JSON string from the triple backticks
      const jsonStart = generatedText.indexOf('```json') + 7; // Start of JSON
      const jsonEnd = generatedText.lastIndexOf('```'); // End of JSON
      const jsonString = generatedText.slice(jsonStart, jsonEnd).trim();

      // Parse the JSON string into an object
      const updatedStoryData = JSON.parse(jsonString);

      setEnhancedStory(updatedStoryData); // Set enhanced story instead of main story
    } catch (error) {
      console.error('Error enhancing story:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsEnhancing(false); // Stop enhancement loading
    }
  };

  const handleUploadStory = (generatedText) => {
    setIsEnhancing(true); // Start enhancement loading
    try {
      console.log('generatedText', generatedText)

      const jsonString = generatedText.trim();

      // Parse the JSON string into an object
      const updatedStoryData = JSON.parse(jsonString);

      setEnhancedStory(updatedStoryData); // Set enhanced story instead of main story
    } catch (error) {
      console.error('Error enhancing story:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsEnhancing(false); // Stop enhancement loading
    }
  };

  const handleConfirm = () => {
    setIsEditing(false); // Switch to story playback mode
  };

  const handleNewStory = () => {
    setStory(null); // Clear the story and go back to the summary input
  };

  const handleRemoveDialogue = (index) => {
    const updatedDialogue = story.dialogue.filter((_, i) => i !== index);
    setStory({ ...story, dialogue: updatedDialogue });
  };

  const handleAddDialogue = (index) => {
    const newDialogue = { character: 'Fox', text: 'New dialogue added here.' }; // Default new dialogue
    const updatedDialogue = [
      ...story.dialogue.slice(0, index + 1),
      newDialogue,
      ...story.dialogue.slice(index + 1),
    ];
    setStory({ ...story, dialogue: updatedDialogue });
  };

  const handleEditDialogue = (index, newText, newCharacter = null) => {
    const updatedDialogue = story.dialogue.map((dialogue, i) =>
      i === index
        ? { ...dialogue, text: newText, character: newCharacter || dialogue.character }
        : dialogue
    );
    setStory({ ...story, dialogue: updatedDialogue });
  };

  const handleAddCharacter = (newCharacter) => {
    const updatedCharacters = [...story.characters, newCharacter];
    setStory({ ...story, characters: updatedCharacters });
  };

  const handleUpdateCharacterImage = (characterName, image) => {
    // Update the character's image in the assets store
    setAssets((prevAssets) => ({
      ...prevAssets,
      characters: {
        ...prevAssets.characters,
        [characterName]: image, // Add or update the character's image
      },
    }));

    // No longer update the character's image in the story state
  };

  // Function to update the background image
  const handleUpdateBackgroundImage = (image) => {
    setAssets((prevAssets) => ({
      ...prevAssets,
      backgroundImage: image, // Update the background image
    }));
  };

  // Function to update the background music
  const handleUpdateBackgroundMusic = (music) => {
    setAssets((prevAssets) => ({
      ...prevAssets,
      backgroundMusic: music, // Update the background music
    }));
  };

  const handleGoBackToStoryboard = () => {
    setIsEditing(true); // Switch back to storyboard mode
  };

  // Clear summaries when a summary is selected
  const clearSummaries = () => {
    setSummaries([]);
  };

  // New function to handle input and decide what to do
  const handleGenerate = (input) => {
    try {
      // Try to parse the input as JSON
      const parsedData = JSON.parse(input);

      // Check if the parsed data matches the expected format
      if (
        parsedData.title !== undefined &&
        Array.isArray(parsedData.characters) &&
        Array.isArray(parsedData.dialogue)
      ) {

        // Ensure all characters in dialogue are present in the characters array
        const updatedCharacters = [...parsedData.characters]; // Start with existing characters

        parsedData.dialogue.forEach((dialogue) => {
          const characterName = dialogue.character;

          // Check if the character already exists in the characters array
          const characterExists = updatedCharacters.some(
            (character) => character.name === characterName
          );

          // If the character doesn't exist, add it to the characters array
          if (!characterExists) {
            updatedCharacters.push({
              name: characterName,
              description: `A new character: ${characterName}`, // Default description
            });
          }
        });

        // Update the parsedData with the updated characters array
        parsedData.characters = updatedCharacters;

        // If the format is correct, set the story
        setStory(parsedData);

      } else {
        // If the format is incorrect, treat it as a summary
        if (input.length < 75) {
          handleGenerateSummaries(input); // Call handleGenerateSummaries for short inputs
        } else {
          handleSummarySelection(input); // Call handleSummarySelection for longer inputs
        }
      }
    } catch (error) {
      // If parsing fails, treat it as a summary
      if (input.length < 75) {
        handleGenerateSummaries(input); // Call handleGenerateSummaries for short inputs
      } else {
        handleSummarySelection(input); // Call handleSummarySelection for longer inputs
      }
    }
  };

  return (
    <Router>
      <div>
        <Routes>
          {/* ✅ Load Home page directly */}
          <Route path="/" element={<Home />} />

          {/* ✅ Handle Play page logic */}
          <Route path="/play/:storyId" element={<PlayPage />} />

          {/* ✅ Handle /create logic */}
          <Route
            path="/create"
            element={
              story ? (
                isEditing ? (
                  <Storyboard
                    story={story}
                    enhancedStory={enhancedStory}
                    onConfirm={handleConfirm}
                    onRemoveDialogue={handleRemoveDialogue}
                    onAddDialogue={handleAddDialogue}
                    onEditDialogue={handleEditDialogue}
                    onAddCharacter={handleAddCharacter}
                    onEnhanceStory={handleEnhanceStory}
                    isEnhancing={isEnhancing} // Pass isEnhancing to Storyboard
                    onSelectStory={handleSelectStory} // Add this line
                    onUpdateCharacterImage={handleUpdateCharacterImage} // Pass the new function
                    assets={assets}
                    onUploadStory={handleUploadStory}
                    onUpdateBackgroundImage={handleUpdateBackgroundImage}
                    onUpdateBackgroundMusic={handleUpdateBackgroundMusic}
                  />
                ) : (
                  <StoryPlayback
                    storyId={'create'}
                    story={story}
                    onReplay={() => setIsEditing(true)}
                    onNewStory={handleNewStory}
                    onBackPress={handleGoBackToStoryboard}
                    assets={assets}
                  />
                )
              ) : (
                <SummaryInput
                  onGenerate={handleGenerate} // Use the new handleGenerate function
                  onUserPreFedStory={onPreFedStory}
                  isLoading={isLoading}
                  summaries={summaries} // Pass summaries to SummaryInput
                  onSelectSummary={(selectedSummary) => {
                    handleSummarySelection(selectedSummary); // Call the existing function
                    clearSummaries(); // Clear summaries after selection
                  }}
                />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;